import { ReactNode } from "react";
import { useParams } from "react-router-dom";

import Badge from "components/Badge";
import KnowledgeBaseCallout from "components/KnowledgeBaseCallout";
import Loading from "components/Loading";
import NotFound from "components/NotFound/NotFound";
import PageHeader from "components/PageHeader";
import { useGET } from "hooks/useAPI";
import { PortalContent } from "layouts/PortalLayout";

import PolicyQuoteLanding from "./PolicyQuoteLanding";
import {
  parsePolicyView,
  STATUS_PILL,
  formatIssueDate,
  statusMessage,
  type PolicyStatus,
  type PolicyView,
} from "./policy-view";

const PILL_TONE: Record<PolicyStatus, "yellow" | "green" | "blue"> = {
  finalizing: "yellow",
  issued: "green",
  other: "blue",
};

function PolicyFrame({ children }: { children: ReactNode }) {
  return <PortalContent>{children}</PortalContent>;
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1 py-4 sm:flex-row sm:gap-6">
      <dt className="text-sm font-bold text-evergreen sm:w-44 sm:flex-none">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-base text-soil">{value}</dd>
    </div>
  );
}

function PolicyBody({
  view,
  policyId,
}: {
  view: PolicyView;
  policyId: string;
}) {
  const message = statusMessage(view);
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Policy"
        title={view.insuredName ?? "Your policy"}
        backLink={{ to: "/", label: "Home" }}
      />
      <div className="flex flex-col items-start gap-3">
        <Badge
          status={PILL_TONE[view.status]}
          text={
            view.status === "other"
              ? (view.stage ?? STATUS_PILL.other)
              : STATUS_PILL[view.status]
          }
        />
        {message && <p className="text-lg text-oat-700">{message}</p>}
      </div>
      <dl className="divide-y divide-oat-200 rounded-xl border border-oat-200 bg-white px-5">
        <InfoRow label="Plan" value={view.planName} />
        <InfoRow label="Carrier" value={view.carrierName} />
        <InfoRow label="Insured" value={view.insuredName} />
        <InfoRow label="Policy number" value={view.policyNumber} />
        <InfoRow label="Issued" value={formatIssueDate(view.issueDate)} />
        <InfoRow label="Payment" value={view.paymentPeriod} />
      </dl>
      <KnowledgeBaseCallout subject={{ kind: "policy", id: policyId }} />
    </div>
  );
}

/** The quote-stage landing while the policy's quote is active, the policy
 * once it is not. */
function PolicyContent({
  view,
  policyId,
}: {
  view: PolicyView;
  policyId: string;
}) {
  if (view.quoteId !== null) {
    return (
      <PolicyQuoteLanding
        view={view}
        policyId={policyId}
        quoteId={view.quoteId}
      />
    );
  }
  return <PolicyBody view={view} policyId={policyId} />;
}

export default function PolicyDetailsPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const { data, isLoading, error } = useGET(
    policyId ? `/policies/${encodeURIComponent(policyId)}` : null,
    undefined,
    {
      shouldRetryOnError: (err: { statusCode?: number }) =>
        err?.statusCode !== 404,
    },
  );

  if (isLoading) {
    return (
      <PolicyFrame>
        <Loading message="Loading your policy..." />
      </PolicyFrame>
    );
  }

  if (error || !data || !policyId) {
    const isNotFound = error?.statusCode === 404;
    return (
      <PolicyFrame>
        <div className="my-16 flex items-center justify-center px-3">
          <NotFound
            title={isNotFound ? "Policy Not Found" : "Error Loading Policy"}
            message={
              isNotFound
                ? "Sorry, we couldn't find the policy you're looking for."
                : "Sorry, there was a problem loading your policy. Please try again later."
            }
          />
        </div>
      </PolicyFrame>
    );
  }

  return (
    <PolicyFrame>
      <PolicyContent view={parsePolicyView(data)} policyId={policyId} />
    </PolicyFrame>
  );
}
